import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  Descriptions,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  getCampaign,
  listCampaignRecipients,
  type RecipientItem,
} from '../../services/bulk-email.service';
import { CAMPAIGN_STATUS_LABELS, RECIPIENT_STATUS_LABELS } from '../../constants/bulk-email.constants';

const { Title, Text } = Typography;

export const BulkEmailCampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const campaignId = Number(id);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);

  const {
    data: campaign,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['bulk-email-campaign', campaignId],
    queryFn: () => getCampaign(campaignId),
    enabled: Number.isFinite(campaignId),
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.status === 'PENDING' || data?.status === 'PROCESSING' ? 5000 : false;
    },
  });

  const { data: recipients, isLoading: recipientsLoading } = useQuery({
    queryKey: ['bulk-email-campaign-recipients', campaignId, statusFilter, page],
    queryFn: () =>
      listCampaignRecipients(campaignId, { status: statusFilter, page, pageSize: 50 }),
    enabled: Number.isFinite(campaignId),
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <Alert
        type="error"
        message="Erro ao carregar campanha"
        description="Não foi possível carregar os dados desta campanha."
        action={
          <Button onClick={() => refetch()} icon={<ReloadOutlined />}>
            Tentar novamente
          </Button>
        }
      />
    );
  }

  const isPending = campaign.status === 'PENDING' || campaign.status === 'PROCESSING';
  const statusInfo = CAMPAIGN_STATUS_LABELS[campaign.status] ?? { label: campaign.status, color: 'default' };

  const columns = [
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const info = RECIPIENT_STATUS_LABELS[status] ?? { label: status, color: 'default' };
        return <Badge status={info.color as any} text={info.label} />;
      },
    },
    { title: 'Motivo da falha', dataIndex: 'failureReason', key: 'failureReason', render: (v: string | null) => v ?? '-' },
  ];

  return (
    <div className="admin-bulk-email-campaign-detail">
      <Space style={{ marginBottom: 8 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/bulk-email')}>
          Voltar
        </Button>
      </Space>

      <Card title="Campanha de Comunicação em Massa">
        <Descriptions column={3} layout="vertical" size="small">
          <Descriptions.Item label="Status">
            <Badge status={statusInfo.color as any} text={statusInfo.label} />
          </Descriptions.Item>
          <Descriptions.Item label="Origem">{campaign.source}</Descriptions.Item>
          <Descriptions.Item label="Disparado por">{campaign.adminUserName}</Descriptions.Item>
          <Descriptions.Item label="Assunto" span={3}>{campaign.subject}</Descriptions.Item>
          <Descriptions.Item label="Total de destinatários">{campaign.totalRecipients}</Descriptions.Item>
          <Descriptions.Item label="Enviados">{campaign.sentCount}</Descriptions.Item>
          <Descriptions.Item label="Falhos">{campaign.failedCount}</Descriptions.Item>
          <Descriptions.Item label="Suprimidos">{campaign.suppressedCount}</Descriptions.Item>
        </Descriptions>
      </Card>

      {isPending && (
        <Card style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: 32 }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} />} />
            <Title level={4}>Envio em processamento</Title>
            <Text type="secondary">
              Os emails estão sendo enviados em segundo plano. Esta página é atualizada automaticamente.
            </Text>
          </div>
        </Card>
      )}

      <Card title="Destinatários" style={{ marginTop: 16 }}>
        <Space style={{ marginBottom: 12 }}>
          <Select
            allowClear
            placeholder="Filtrar por status"
            style={{ width: 220 }}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}
            options={Object.entries(RECIPIENT_STATUS_LABELS).map(([value, { label }]) => ({
              value,
              label,
            }))}
          />
        </Space>
        <Table<RecipientItem>
          rowKey="id"
          loading={recipientsLoading}
          columns={columns}
          dataSource={recipients?.items ?? []}
          pagination={{
            current: page,
            pageSize: 50,
            total: recipients?.totalItems ?? 0,
            onChange: setPage,
          }}
        />
      </Card>
    </div>
  );
};
